import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { firstValueFrom, Observable, Subject } from 'rxjs';

import { MsdAiResult } from '../../../core/domain/msd-ai-result';
import { Diary } from '../../tracker/domain/diary/diary';
import {
  DiaryRepository,
  DiarySchema,
} from '../../tracker/repositories/diary_repository';
import { MsdAiEvent } from '../events/msd-ai-events';

@Injectable()
export class MsdAiService {
  private readonly diaryAnalysisCloud: (data: unknown) => Observable<void>;
  private readonly diaryAnalysisForMsdAiResultsCloud: (data: {
    diary: Diary;
    dietitianId: string;
  }) => Observable<MsdAiResult[]>;
  private readonly diaryAnalysisDescriptionForIngestasCloud: (data: {
    result: MsdAiResult;
    patientId: string;
  }) => Observable<MsdAiResult>;
  eventsSubject = new Subject<MsdAiEvent>();

  constructor(
    private functions: AngularFireFunctions,
    private diaryRepository: DiaryRepository,
  ) {
    this.diaryAnalysisCloud = this.functions.httpsCallable<unknown, void>(
      'msd_ai-diaryAnalysis',
    );

    this.diaryAnalysisForMsdAiResultsCloud = this.functions.httpsCallable<
      { diary: Diary; dietitianId: string },
      MsdAiResult[]
    >('msd_ai-diaryAnalysisForMsdAiResults');

    this.diaryAnalysisDescriptionForIngestasCloud =
      this.functions.httpsCallable<
        {
          result: MsdAiResult;
          patientId: string;
        },
        MsdAiResult
      >('msd_ai-diaryAnalysisDescriptionForIngestas');
  }

  async diaryAnalysis(
    dietitianId: string,
    patientId: string,
    diaryId: string,
  ): Promise<void> {
    await firstValueFrom(
      this.diaryAnalysisCloud({ dietitianId, patientId, diaryId }),
    );
  }

  async diaryAnalysisForMsdAiResults(
    diary: Diary,
    dietitianId: string,
  ): Promise<MsdAiResult[]> {
    return await firstValueFrom(
      this.diaryAnalysisForMsdAiResultsCloud({ diary, dietitianId }),
    );
  }

  diaryValueChanges(
    patientId: string,
    diaryId: string,
  ): Observable<DiarySchema | undefined> {
    return this.diaryRepository.valueChanges(patientId, diaryId);
  }

  get events$(): Observable<MsdAiEvent> {
    return this.eventsSubject.asObservable();
  }

  dispatch(event: MsdAiEvent) {
    this.eventsSubject.next(event);
  }

  async diaryAnalysisDescriptionForIngestas(
    msdAiResult: MsdAiResult,
    patientId: string,
  ): Promise<MsdAiResult> {
    return await firstValueFrom(
      this.diaryAnalysisDescriptionForIngestasCloud({
        result: msdAiResult,
        patientId,
      }),
    );
  }
}
